import React from 'react';
import { Box, Typography, Chip } from '@mui/material';

/*
 * Shared chrome for the two custom-application connection panels.
 *
 * SAML and OIDC panels show different FIELDS -- that difference is real and deliberate.
 * What is not deliberate is showing them in different visual languages, which is what
 * happened when the OIDC panel was added beside the SAML branch and only the new one was
 * styled: same feature, same screen, two idioms depending on a protocol the admin did not
 * choose for design reasons.
 *
 * Both panels now compose the heading and card styles from here, so a change to one is a
 * change to both. Keeping these in either protocol's own file is what let them diverge.
 */

/*
 * One accent per protocol, so the two panels are distinguishable at a glance without being
 * differently designed. These were already chosen -- violet for SAML, teal for OIDC -- but
 * lived as private constants in two files with no shared reference.
 */
export const PROTOCOL_ACCENTS = {
  saml: { main: '#7B5EA7', light: '#F1ECF9' },
  'openid-connect': { main: '#1F7A8C', light: '#E8F4F6' },
};

/** Accent lookup that never returns undefined -- an unknown protocol falls back to SAML. */
export const accentFor = (protocol) => PROTOCOL_ACCENTS[protocol] || PROTOCOL_ACCENTS.saml;

export const makeSectionTitleSx = (accent) => ({
  color: accent,
  fontFamily: 'Bitter, Georgia, serif',
  fontWeight: 600,
  fontSize: '1rem',
});

export const sectionHintSx = {
  color: '#666666',
  fontFamily: 'Metropolis, sans-serif',
  fontSize: '0.8rem',
  mt: 0.5,
  mb: 2,
};

export const panelPaperSx = {
  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  border: '1px solid #E5E5E5',
  p: 3,
};

/**
 * A panel section heading: title, an optional protocol chip, and the one-line explanation
 * of what the values below have in common.
 *
 * The hint is not decoration. Both panels split into "unique to this application" and
 * "identical across your tenant", and that distinction is invisible from the values
 * themselves -- a realm endpoint and a per-client URL look alike. Stating it is what stops
 * an admin copying a realm-wide URL believing it identifies their application.
 */
export const ConnectionSectionHeading = ({ title, chipLabel, accent, accentLight, hint }) => (
  <>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Typography sx={makeSectionTitleSx(accent)}>{title}</Typography>
      {chipLabel && (
        <Chip
          label={chipLabel}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.7rem',
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            backgroundColor: accentLight,
            color: accent,
          }}
        />
      )}
    </Box>
    {hint && <Typography sx={sectionHintSx}>{hint}</Typography>}
  </>
);

export default ConnectionSectionHeading;
